import React, { useContext } from 'react'
import { Button } from '@olxbr/ds-core'
import { Card, Link } from '@olxbr/ds-web'
import { ProfileFeedbackSuccess, Safety, EmailFeedbackSuccess } from '@olxbr/ds-spots'
import { CardRectangle } from '../../atoms/CardRectangle'
import styled from 'styled-components'
import { Verified } from '@olxbr/ds-icons'
import { CpfCnpjUpdate } from '../../organisms/CpfCnpjUpdate'
import { breakpoints } from '../../../util/medias'
import PropTypes from 'prop-types'
import { RevalidationContext } from '../../../util/providers/RevalidationProvider'
import { ErrorAlertbox } from '../../atoms/ErrorAlertbox/ErrorAlertbox'
import { XCircle } from '@olxbr/ds-icons'

const FlexCard = styled(Card)`
  row-gap: var(--spacing-2);
  flex-direction: column;

  ${props =>
    props.error &&
    `opacity: 0.5;
    filter: grayscale(80%);
    pointer-events: none;
    cursor: default;`}
`

const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-2);

  @media (min-width: ${breakpoints.mobile}px) {
    margin-bottom: var(--spacing-3);
  }
`

const LinkText = styled(Link)`
  ${props =>
    props.isLoading &&
    `
  color: var(--color-neutral-120);
  pointer-events: none;
  cursor: default;`}
`

const ErrorMsgWrapper = styled.div`
  display: flex;
`

export const AccountUpdateTemplate = ({
  onClickRevalidatePhone,
  onClickRevalidateEmail,
  onSuccessDocument,
  error,
  afterRevalidation,
  isLoading,
}) => {
  const { email, legalDocument, phone, lurker } = useContext(RevalidationContext)
  const disableButton = !phone.validated || !email.validated || !legalDocument.validated

  const renderLink = (onClick, linkDescription, check = false, isLoadingLink) => {
    return check ? (
      <Verified color="var(--color-feedback-success-100)" size="24" />
    ) : (
      <LinkText tag="button" size="medium" onClick={onClick} isLoading={isLoadingLink}>
        {linkDescription}
      </LinkText>
    )
  }

  const addEventLurker = (evtDetail, parentFunction) => () => {
    lurker.viewMessageEvt(evtDetail)
    parentFunction()
  }

  const onClickGoToSite = () => {
    lurker.viewMessageEvt('go_to_site')
    afterRevalidation()
  }

  const onClickReloadPage = () => globalThis.location.reload()

  return (
    <div className="flex pb-3 flex-col">
      <TitleWrapper>
        <span className="typo-title-medium">Atualize seus dados</span>
      </TitleWrapper>
      <span className="typo-body-medium mb-3">
        Antes de ir para o site, nos ajude a manter a sua conta segura e atualizada. Não se
        preocupe, essa atualização será solicitada esporadicamante.
      </span>
      <span className="typo-body-medium mb-3">
        Confirme alguns dados importantes para a sua conta:
      </span>
      {error && (
        <ErrorMsgWrapper>
          <ErrorAlertbox title="Ajustes em nosso sistema." customIcon={<XCircle size="24" />}>
            Já estamos trabalhando para resolver. Agradecemos a compreensão e pedimos que tente
            novamente mais tarde.
          </ErrorAlertbox>
        </ErrorMsgWrapper>
      )}
      <FlexCard error={error}>
        <CardRectangle
          icon={<ProfileFeedbackSuccess shape="circle" size="64" />}
          title="Escolha o tipo da sua conta"
          rightComponent={
            legalDocument.validated && (
              <Verified color="var(--color-feedback-success-100)" size="24" />
            )
          }
        >
          <CpfCnpjUpdate onSuccessDocument={onSuccessDocument} isLoadingDocument={isLoading} />
        </CardRectangle>

        <CardRectangle
          icon={<Safety shape="circle" size="64" />}
          title="Telefone"
          rightComponent={renderLink(
            addEventLurker('revalidate_phone', onClickRevalidatePhone),
            'Validar',
            phone.validated,
            !phone.value && isLoading
          )}
          subtitle={phone.value ?? 'Valide seu número de telefone'}
          isLoading={!phone.value && isLoading}
        />

        <CardRectangle
          icon={<EmailFeedbackSuccess shape="circle" size="64" />}
          title="E-mail"
          rightComponent={renderLink(
            addEventLurker('revalidate_email', onClickRevalidateEmail),
            'Validar',
            email.validated,
            !email.value && isLoading
          )}
          subtitle={email.value}
          isLoading={!email.value && isLoading}
        />
      </FlexCard>
      {error ? (
        <div className="flex mt-3 mb-3 justify-center">
          <Button onClick={() => onClickReloadPage()} size="medium">
            Atualizar página
          </Button>
        </div>
      ) : (
        <div className="flex mt-3 mb-3 justify-center">
          <Button onClick={onClickGoToSite} size="medium" disabled={disableButton}>
            Ir para o site
          </Button>
        </div>
      )}
    </div>
  )
}

AccountUpdateTemplate.propTypes = {
  onClickRevalidatePhone: PropTypes.func.isRequired,
  onClickRevalidateEmail: PropTypes.func.isRequired,
  onSuccessDocument: PropTypes.func,
  error: PropTypes.bool,
  afterRevalidation: PropTypes.func,
  isLoading: PropTypes.bool,
}
